"use client";

import React, { useState } from "react";
import Link from "next/link";
import { X, Calendar, Clock, Car, ShieldCheck, ArrowRight, CheckCircle2, Building } from "lucide-react";
import confetti from "canvas-confetti";
import { PropertyItem, FEATURED_PROPERTIES } from "@/lib/demos/propertiesData";

interface SiteVisitModalProps {
  property?: PropertyItem | null;
  currency?: "INR" | "USD";
  onClose: () => void;
  onLeadCreated?: () => void;
}

export default function SiteVisitModal({
  property,
  currency = "INR",
  onClose,
  onLeadCreated,
}: SiteVisitModalProps) {
  const [selectedPropertyId, setSelectedPropertyId] = useState(property?.id || FEATURED_PROPERTIES[0].id);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [visitDate, setVisitDate] = useState("");
  const [timeSlot, setTimeSlot] = useState("11:30 AM");
  const [chauffeurPickup, setChauffeurPickup] = useState(true);
  const [buyerType, setBuyerType] = useState<"End-User" | "Investor" | "NRI">("End-User");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedLeadId, setSubmittedLeadId] = useState<number | null>(null);

  const selectedProp = FEATURED_PROPERTIES.find((p) => p.id === selectedPropertyId) || FEATURED_PROPERTIES[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const budgetFormatted = currency === "INR" ? selectedProp.priceINR : selectedProp.priceUSD;
      const budgetNum = currency === "INR" ? selectedProp.numericINR : selectedProp.numericUSD;

      const payload = {
        name: fullName,
        email,
        phone,
        property_name: selectedProp.title,
        property_type: selectedProp.type,
        budget_range: budgetFormatted,
        budget_numeric: budgetNum,
        currency,
        site_visit_date: `${visitDate || "Upcoming Weekend"} ${timeSlot}`,
        site_visit_pickup: chauffeurPickup,
        buyer_type: buyerType,
        lead_source: "VIP Site Visit Modal",
        notes: notes || `Site inspection booked for ${selectedProp.title}. Preferred time: ${timeSlot}. Chauffeur: ${chauffeurPickup ? "Requested" : "Self-drive"}.`,
      };

      const res = await fetch("/api/demos/real-estate/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        setSubmittedLeadId(data.lead?.id || 999);
        if (onLeadCreated) onLeadCreated();

        confetti({
          particleCount: 120,
          spread: 70,
          origin: { y: 0.6 },
        });
      }
    } catch (err) {
      console.error("Error submitting site visit:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fadeIn">
      <div className="relative w-full max-w-2xl bg-slate-900 border border-amber-500/30 rounded-2xl shadow-2xl overflow-hidden text-slate-100 max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-800 bg-gradient-to-r from-slate-900 via-slate-900 to-amber-950/40 flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400">
              Private Concierge Service
            </span>
            <h2 className="text-xl font-bold text-white flex items-center gap-2 mt-0.5 font-heading">
              <Building className="w-5 h-5 text-amber-400" />
              Schedule a VIP Private Site Inspection
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-grow">
          {submittedLeadId ? (
            <div className="text-center py-8 px-4 flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4 text-emerald-400 animate-bounce">
                <CheckCircle2 className="w-9 h-9" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                VIP Inspection Confirmed!
              </h3>
              <p className="text-slate-300 text-sm max-w-md mb-6 leading-relaxed">
                Your private viewing for <strong className="text-amber-400">{selectedProp.title}</strong> has been logged. 
                {chauffeurPickup ? " Our private chauffeur will arrive at your location 30 minutes prior." : " An estate specialist will receive you on arrival."}
              </p>

              {/* Demo highlight box */}
              <div className="w-full p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-left mb-6">
                <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider block mb-1">
                  ⚡ Real-Time Demo Pipeline Action
                </span>
                <p className="text-xs text-slate-200">
                  This inquiry has been immediately ingested into the <strong>Real Estate Sales CRM</strong> with high priority, stage set to <em>New Inquiries</em>, and calendar hold placed.
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  href="/demos/real-estate/crm"
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold text-sm shadow-lg hover:shadow-amber-500/25 flex items-center gap-2 transition-all hover:scale-[1.02]"
                >
                  <span>Open Real Estate Sales CRM</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-semibold transition-colors"
                >
                  Close & Explore More Properties
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Selected Property Selector */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5 uppercase tracking-wider">
                  Target Property *
                </label>
                <select
                  value={selectedPropertyId}
                  onChange={(e) => setSelectedPropertyId(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-800/90 border border-slate-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                >
                  {FEATURED_PROPERTIES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title} ({p.location}) — {currency === "INR" ? p.priceINR : p.priceUSD}
                    </option>
                  ))}
                </select>
              </div>

              {/* Name & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Your Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Vikramaditya Oberoi"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    WhatsApp Phone Number *
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 98200 12345"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Email & Buyer Persona */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="name@executive.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Buyer Profile *
                  </label>
                  <select
                    value={buyerType}
                    onChange={(e) => setBuyerType(e.target.value as any)}
                    className="w-full px-3.5 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                  >
                    <option value="End-User">End-User (Family Home / Primary Residence)</option>
                    <option value="Investor">High-Yield Investor (Rental / Capital Gain)</option>
                    <option value="NRI">NRI / International Resident</option>
                  </select>
                </div>
              </div>

              {/* Date & Time Slot */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-amber-400" />
                    Preferred Inspection Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={visitDate}
                    onChange={(e) => setVisitDate(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-amber-400" />
                    Preferred Time Slot *
                  </label>
                  <select
                    value={timeSlot}
                    onChange={(e) => setTimeSlot(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                  >
                    <option value="10:00 AM">10:00 AM (Morning Daylight)</option>
                    <option value="11:30 AM">11:30 AM (Mid-Day Viewing)</option>
                    <option value="03:00 PM">03:00 PM (Afternoon)</option>
                    <option value="05:30 PM">05:30 PM (Golden Hour / Sunset Deck)</option>
                  </select>
                </div>
              </div>

              {/* Chauffeur Service Toggle */}
              <div className="p-3.5 rounded-xl bg-slate-800/80 border border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-amber-500/10 text-amber-400">
                    <Car className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-white">
                      Complimentary Private Chauffeur
                    </div>
                    <div className="text-[11px] text-slate-400">
                      Private BMW / Mercedes pickup from your residence or city airport.
                    </div>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={chauffeurPickup}
                  onChange={(e) => setChauffeurPickup(e.target.checked)}
                  className="w-4 h-4 accent-amber-500 rounded cursor-pointer"
                />
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Specific Specifications or Requests (Optional)
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Please arrange floor plan blueprints, Vastu compliance report, or financing options."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3.5 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-xs focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div className="pt-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Strict confidentiality assured. NDA on request.</span>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold text-sm shadow-lg hover:shadow-amber-500/20 transition-all hover:scale-[1.02] disabled:opacity-50"
                >
                  {isSubmitting ? "Registering..." : "Confirm VIP Viewing"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
